import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown, BadgeCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

let cachedPositions = null;

export function PositionCombobox({ value, onChange, placeholder = 'Ej: Gerente de ventas', testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [positions, setPositions] = useState(cachedPositions || []);

  useEffect(() => {
    if (cachedPositions) return;
    fetch(`${API_URL}/api/positions/known`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.positions) {
          cachedPositions = data.positions;
          setPositions(data.positions);
        }
      })
      .catch(() => {});
  }, []);

  const isKnownPosition = positions.includes(value);
  const searchIsKnown = positions.some((p) => p.toLowerCase() === search.trim().toLowerCase());

  const handleSelect = (position) => {
    onChange(position);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className="flex items-center gap-1.5 truncate">
            {value ? (
              <>
                {isKnownPosition && <BadgeCheck className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />}
                <span className="truncate">{value}</span>
              </>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Buscar puesto..."
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty className="px-3 py-3 text-sm text-muted-foreground">
              Sin coincidencias en la lista de puestos con dato real.
            </CommandEmpty>
            <CommandGroup heading="Puestos con dato real (Guía Salarial Buk 2026)">
              {positions.map((position) => (
                <CommandItem key={position} value={position} onSelect={() => handleSelect(position)}>
                  <Check className={cn('mr-2 h-4 w-4', value === position ? 'opacity-100' : 'opacity-0')} />
                  {position}
                </CommandItem>
              ))}
            </CommandGroup>
            {search.trim() && !searchIsKnown && (
              <CommandGroup heading="Puesto sin dato verificado">
                <CommandItem
                  value={`usar-texto-libre-${search}`}
                  onSelect={() => handleSelect(search.trim())}
                  data-testid={testId ? `${testId}-free-text` : undefined}
                >
                  Usar &quot;{search.trim()}&quot;
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default PositionCombobox;
